/**
 * Dashboard — COORD-26
 *
 * Personalized 4-quadrant ambient monitor. Theme-aware (light/dark).
 * Auto-refresh 5min. Single GET /api/dashboard/command call.
 * Route: /home. Same layout shell.
 *
 * Quadrants:
 *   TL: My Accountability — overdue/due tasks by urgency
 *   TR: Pipeline Pulse — stage counts, throughput, attention deals
 *   BL: Response Tracker — deadlines with countdown + status
 *   BR: Intelligence Monitor — last 48h high-relevance OSINT
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import DashboardCard from "../components/dashboard/DashboardCard";
import AgencyBadge from "../components/dashboard/AgencyBadge";
import UrgencyBadge from "../components/dashboard/UrgencyBadge";
import HealthDot from "../components/dashboard/HealthDot";
import StatusBadge from "../components/dashboard/StatusBadge";
import Tooltip from "../components/Tooltip";
import InfoIcon from "../components/InfoIcon";
import TaskDismissModal from "../components/TaskDismissModal";
import {
  CheckSquare, ChartBar, FileText, Globe, UsersThree,
  CheckCircle, Envelope, SpeakerSimpleX, Warning, X,
  CalendarBlank,
} from "@phosphor-icons/react";

const REFRESH_MS = 5 * 60 * 1000; // 5 minutes

// ── Quadrant icons (Phosphor, duotone for visual depth) ─────────────────
const ICON_ACCOUNTABILITY = <CheckSquare size={18} weight="duotone" />;
const ICON_PIPELINE       = <ChartBar    size={18} weight="duotone" />;
const ICON_RESPONSES      = <FileText    size={18} weight="duotone" />;
const ICON_INTELLIGENCE   = <Globe       size={18} weight="duotone" />;
const ICON_STAFFING       = <UsersThree  size={18} weight="duotone" />;

// ── Helpers ─────────────────────────────────────────────────────────────

function fmtMoney(val) {
  if (val == null) return "--";
  const n = typeof val === "string" ? parseFloat(val) : val;
  if (isNaN(n)) return "--";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

function fmtDate(isoStr) {
  if (!isoStr) return "--";
  // Date-only strings (YYYY-MM-DD) have no timezone info — parsing them bare
  // makes JS treat them as UTC midnight, which rolls back a day in any
  // timezone behind UTC (e.g. Colombia). Anchor to local midnight instead.
  const d = /^\d{4}-\d{2}-\d{2}$/.test(isoStr) ? new Date(`${isoStr}T00:00:00`) : new Date(isoStr);
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${String(d.getFullYear()).slice(-2)}`;
}

function fmtTime(d) {
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

function fmtAge(hours) {
  if (hours < 1) return `${Math.round(hours * 60)}m ago`;
  if (hours < 24) return `${Math.round(hours)}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

// ── Intel type colors (light / dark) ───────────────────────────────────
// Desaturated — max 80% saturation, no pure AI-violet
const INTEL_COLORS = {
  competitive: { light: "#b91c1c", dark: "#fca5a5" },
  stakeholder: { light: "#1d4ed8", dark: "#93c5fd" },
  proactive:   { light: "#0e7490", dark: "#67e8f9" },
  market:      { light: "#15803d", dark: "#86efac" },
  staffing:    { light: "#b45309", dark: "#fcd34d" },
};

function HeaderInfoIcon({ tooltip }) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  const handleMouseEnter = () => {
    timerRef.current = setTimeout(() => setVisible(true), 300);
  };

  const handleMouseLeave = () => {
    clearTimeout(timerRef.current);
    setVisible(false);
  };

  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        marginLeft: "8px",
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full border border-gray-300 text-gray-400 hover:text-gray-600 hover:border-gray-400 cursor-help transition-colors text-[9px] font-bold leading-none select-none flex-shrink-0">
        i
      </span>
      <span
        style={{
          position: "absolute",
          top: "100%",
          left: "50%",
          width: "260px",
          marginTop: "8px",
          transform: "translateX(-50%)",
          zIndex: 60,
          pointerEvents: "none",
          opacity: visible ? 1 : 0,
          transition: "opacity 150ms",
        }}
        role="tooltip"
      >
        <span
          className="bg-gray-900 text-white text-[11px] leading-snug rounded-md shadow-xl px-2 py-1"
          style={{
            display: "block",
            whiteSpace: "normal",
          }}
        >
          {tooltip}
        </span>
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 border-b-gray-900 border-x-transparent border-t-transparent border-4 w-0 h-0" />
      </span>
    </span>
  );
}

// ── Main Component ──────────────────────────────────────────────────────

export default function UserDashboard() {
  const { hasPermission, user } = useAuth();
  const canSeePipeline  = hasPermission("leads.view");
  const canSeeResponses = hasPermission("leads.view");
  const canSeeStaffing  = hasPermission("leads.view");
  const canSeeIntel     = hasPermission("intel.view");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const [newItemIds, setNewItemIds] = useState(new Set());
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const [dismissModal, setDismissModal] = useState(null);
  const prevIdsRef = useRef(new Set());
  const [holidays, setHolidays] = useState([]);

  // Detect theme for dynamic color selection (re-render on toggle via MutationObserver)
  const [isDark, setIsDark] = useState(document.documentElement.classList.contains("dark"));
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const fetchData = useCallback(async (isInitial = false) => {
    try {
      const res = await fetch("/api/dashboard/command", { credentials: "include" });
      if (res.ok) {
        const json = await res.json();

        // Track new items for highlight
        if (!isInitial && data) {
          const currentIds = new Set();
          json.accountability?.items?.forEach(i => currentIds.add(i.task_id));
          json.pipeline?.attention?.forEach(i => currentIds.add(i.opportunity_id));
          json.responses?.items?.forEach(i => currentIds.add(i.opportunity_id));
          json.intelligence?.items?.forEach(i => currentIds.add(i.intel_id));

          const newIds = new Set();
          currentIds.forEach(id => {
            if (!prevIdsRef.current.has(id)) newIds.add(id);
          });

          if (newIds.size > 0) {
            setNewItemIds(newIds);
            setTimeout(() => setNewItemIds(new Set()), 2000);
          }

          prevIdsRef.current = currentIds;
        } else {
          const ids = new Set();
          json.accountability?.items?.forEach(i => ids.add(i.task_id));
          json.pipeline?.attention?.forEach(i => ids.add(i.opportunity_id));
          json.responses?.items?.forEach(i => ids.add(i.opportunity_id));
          json.intelligence?.items?.forEach(i => ids.add(i.intel_id));
          prevIdsRef.current = ids;
        }

        setData(json);
      }
    } catch {
      // Silent retry on next poll
    }
    if (isInitial) setLoading(false);
  }, [data]);

  useEffect(() => {
    fetchData(true);
    const iv = setInterval(() => fetchData(false), REFRESH_MS);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update clock every minute
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(iv);
  }, []);

  // Fetch upcoming holidays, filtered to the user's jurisdiction (US / CO).
  // Jurisdiction is resolved from the user's personnel record (front-end only).
  useEffect(() => {
    (async () => {
      try {
        // Resolve the current user's jurisdiction from their personnel record.
        let userJurisdiction = "US";
        if (user?.personnel_id) {
          try {
            const pRes = await fetch(`/api/people/personnel/${user.personnel_id}`, { credentials: "include" });
            if (pRes.ok) {
              const person = await pRes.json();
              userJurisdiction =
                person.jurisdiction ||
                (person.employment_type === "colombian_labor_contract" ? "CO" : "US");
            }
          } catch { /* fall back to US */ }
        }

        const year = new Date().getFullYear();
        const res = await fetch(`/api/timekeeping/holidays?year=${year}`, { credentials: "include" });
        if (!res.ok) return;
        const all = await res.json();
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const upcoming = all
          .filter((h) => new Date(h.holiday_date + "T00:00:00") >= today)
          // Show holidays for the user's jurisdiction plus any that apply everywhere.
          .filter((h) => !h.jurisdiction || h.jurisdiction === userJurisdiction)
          .slice(0, 3);
        setHolidays(upcoming);
      } catch { /* silent */ }
    })();
  }, [user?.personnel_id]);

  // ── Loading Skeleton ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="bg-neutral-50 dark:bg-slate-950 min-h-screen p-6 lg:p-8">
        <div className="h-[60px] rounded-2xl bg-white dark:bg-slate-800 mb-6 shadow-sm" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-[300px] rounded-xl animate-pulse bg-white dark:bg-slate-800 shadow-brand" />
          ))}
        </div>
      </div>
    );
  }

  const openDismissModal = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    setDismissModal(item);
  };

  const { accountability = {}, pipeline = {}, responses = {}, intelligence = {}, staffing = {} } = data || {};
  const pipelineValue = pipeline.total_pipeline_value;
  const lastRefresh = data?.generated_at ? new Date(data.generated_at) : null;

  return (
    <div
      data-tour="dashboard-home"
      className="bg-neutral-50 dark:bg-slate-950 min-h-screen text-neutral-900 dark:text-slate-200"
    >
      {/* ── Header Bar ──────────────────────────────────────────────── */}
      <header className="flex flex-wrap items-center justify-between gap-4 px-5 sm:px-8 py-5 border-b border-neutral-200 dark:border-slate-700 bg-white dark:bg-slate-800/95 shadow-sm dark:shadow-none">
        <div className="flex items-center gap-3 min-w-0">
          <div className="hidden sm:flex h-8 w-auto items-center gap-2.5 pl-1">
            {isDark ? (
              <>
                <img
                  src={`${import.meta.env.BASE_URL}brand/rs-mark-white.png`}
                  alt=""
                  className="h-8 w-8 shrink-0 object-contain opacity-95"
                />
                <span className="shrink-0 text-lg font-bold leading-none tracking-tight text-white">
                  Resolute
                </span>
              </>
            ) : (
              <img
                src={`${import.meta.env.BASE_URL}brand/resolute-full-logo.png`}
                alt="Resolute"
                className="h-8 w-auto max-w-[160px] object-contain object-left opacity-95"
              />
            )}
          </div>
          <div className="flex items-center gap-3 sm:border-l sm:border-neutral-200 sm:dark:border-slate-600 sm:pl-3">
            <div className="w-1 h-8 rounded-full bg-brand-blue flex-shrink-0 shadow-sm" />
            <div>
              <h1 className="text-sm font-semibold tracking-tight text-brand-navy dark:text-slate-100">
                Dashboard
              </h1>
              <p className="text-[11px] font-medium text-neutral-500 dark:text-slate-400">Resolute Solutions</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 flex-wrap justify-end">
          {canSeePipeline && pipelineValue != null && (
            <Tooltip text="Total estimated value of all opportunities in Qualifying through Submitted stages." inline position="bottom">
              <span className="text-lg font-bold tabular-nums text-brand-blue dark:text-blue-300">
                {fmtMoney(pipelineValue)}
              </span>
            </Tooltip>
          )}
          <span className="text-[11px] font-medium tabular-nums text-neutral-600 dark:text-slate-400">
            {now.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
            {" "}
            {fmtTime(now)}
          </span>
          {lastRefresh && (
            <span className="text-[9px] font-medium text-neutral-400 dark:text-slate-500">
              refreshed {fmtTime(lastRefresh)}
            </span>
          )}
          <Tooltip text="System health indicator. Green = all background services running normally." inline position="bottom">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-emerald-500/30" />
          </Tooltip>
        </div>
      </header>

      {/* ── 4-Quadrant Grid — cols collapse to 1 for people-only users ── */}
      <div className={`p-5 sm:p-6 lg:p-8 grid grid-cols-1 gap-6 lg:gap-8 ${canSeePipeline || canSeeIntel ? "lg:grid-cols-2" : ""}`}>
        {/* ── Q1: My Accountability (top-left) ──────────────────────── */}
        <div className="flex flex-col">
          <DashboardCard
            title={
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                My accountability
                <HeaderInfoIcon tooltip="Tasks assigned to you that are overdue or due within 7 days. When this is empty, you're caught up." />
              </span>
            }
            icon={ICON_ACCOUNTABILITY}
            subtitle={accountability.overdue_count > 0
              ? `${accountability.overdue_count} overdue`
              : "All clear"}
          >
            {(() => {
              const ACCOUNTABILITY_VISIBLE_LIMIT = 3;
              const allVisibleItems = (accountability.items || []).filter(
                (item) => !dismissedIds.has(item.task_id)
              );
              const visibleItems = allVisibleItems.slice(0, ACCOUNTABILITY_VISIBLE_LIMIT);
              const remainingCount = allVisibleItems.length - visibleItems.length;
              return allVisibleItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-2">
                  <CheckCircle size={36} weight="thin" className="text-emerald-400 dark:text-emerald-500" />
                  <span className="text-xs font-medium text-neutral-500 dark:text-slate-400">All caught up</span>
                  <span className="text-[11px] text-neutral-400 dark:text-slate-500">No tasks due within 7 days</span>
                </div>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-slate-700">
                  {visibleItems.map((item) => {
                    const handleTaskClick = (e) => {
                      if (item.action_url === "__open_focus_notes__") {
                        e.preventDefault();
                        window.dispatchEvent(new CustomEvent("openWeeklyFocus"));
                      }
                    };
                    // Migration 0224 — MR-source tasks deep-link to the
                    // marketing-response detail page; other tasks keep the
                    // opportunity-detail fallback so existing flows are
                    // unchanged.
                    const isMarketingResponseTask =
                      item.source &&
                      [
                        "mr_techreview",
                        "mr_submission",
                        "mr_analyst",
                        "mktresp_promote",
                        "mktresp_timer",
                        "mktresp_co30",
                        "mktresp_sam59",
                      ].includes(item.source);
                    let href = "#";
                    if (item.action_url === "__open_focus_notes__") {
                      href = "#";
                    } else if (isMarketingResponseTask && item.workflow_item_id) {
                      href = `/growth/marketing-responses/${item.workflow_item_id}`;
                    } else if (item.opportunity_id) {
                      href = `/crm/opportunity/${item.opportunity_id}`;
                    }
                    return (
                      <div
                        key={item.task_id}
                        className={`group flex items-center gap-3 px-4 py-2.5 transition-all hover:bg-gray-50 dark:hover:bg-slate-700/50 ${
                          item.days_remaining < 0 ? "bg-red-50/50 dark:bg-red-900/10" : ""
                        } ${newItemIds.has(item.task_id) ? "border-l-2 border-l-brand-blue" : ""}`}
                      >
                        <a
                          href={href}
                          onClick={handleTaskClick}
                          target={item.action_url === "__open_focus_notes__" ? undefined : "_blank"}
                          rel={item.action_url === "__open_focus_notes__" ? undefined : "noopener noreferrer"}
                          className="flex items-center gap-3 flex-1 min-w-0"
                        >
                          <Tooltip text="How soon this task is due. Overdue items need immediate attention." inline>
                            <UrgencyBadge daysRemaining={item.days_remaining} />
                          </Tooltip>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              {item.display_agency && <AgencyBadge agency={item.display_agency} />}
                              {item.action_url === "__open_focus_notes__" ? (
                                <span className="text-[12px] font-medium truncate text-amber-700 dark:text-amber-400">
                                  {"\u{1F4CB}"} Focus Notes
                                </span>
                              ) : (
                                <span className="text-[12px] font-medium truncate text-gray-900 dark:text-slate-200">
                                  {item.opportunity_title || "No opportunity"}
                                </span>
                              )}
                            </div>
                            <p className="text-[11px] truncate text-gray-500 dark:text-slate-400">
                              {item.task_title}
                            </p>
                          </div>
                          {item.deal_health && (
                            <Tooltip text="Deal health: Green = healthy, Amber = watch, Red = at risk. Based on activity recency, task completion, and deadline proximity." inline>
                              <HealthDot health={item.deal_health} />
                            </Tooltip>
                          )}
                        </a>
                        <Tooltip text="Dismiss with reason" inline>
                          <button
                            onClick={(e) => openDismissModal(e, item)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 w-5 h-5 flex items-center justify-center rounded hover:bg-neutral-200 dark:hover:bg-slate-600 text-neutral-400 hover:text-neutral-600 dark:hover:text-slate-200"
                          >
                            <X size={12} weight="bold" />
                          </button>
                        </Tooltip>
                      </div>
                    );
                  })}
                  {remainingCount > 0 && (
                    <a
                      href="/coord/tasks"
                      className="block text-center text-[11px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline py-2"
                    >
                      +{remainingCount} more &middot; View all
                    </a>
                  )}
                </div>
              );
            })()}
          </DashboardCard>

          {/* ── Upcoming Holidays (compact widget below Accountability) ── */}
          {holidays.length > 0 && (
            <div className="mt-3 rounded-xl bg-white dark:bg-slate-800 border border-neutral-200 dark:border-slate-700 shadow-sm overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-neutral-100 dark:border-slate-700">
                <CalendarBlank size={15} weight="duotone" className="text-indigo-500 dark:text-indigo-400" />
                <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-slate-400">
                  Upcoming Holidays
                </span>
              </div>
              <div className="divide-y divide-neutral-100 dark:divide-slate-700">
                {holidays.map((h) => {
                  const dt = new Date(h.holiday_date + "T00:00:00");
                  const todayCheck = new Date();
                  todayCheck.setHours(0, 0, 0, 0);
                  const diffDays = Math.round((dt - todayCheck) / 86400000);
                  const dayLabel = diffDays === 0 ? "Today" : diffDays === 1 ? "Tomorrow" : `${diffDays}d`;
                  const jur = h.jurisdiction || "US";
                  return (
                    <div key={h.id} className="flex items-center gap-3 px-4 py-2">
                      <div className="flex flex-col items-center justify-center flex-shrink-0 w-10">
                        <span className={`text-sm font-bold tabular-nums ${
                          diffDays === 0 ? "text-indigo-600 dark:text-indigo-400"
                            : diffDays <= 7 ? "text-emerald-600 dark:text-emerald-400"
                            : "text-neutral-500 dark:text-slate-400"
                        }`}>
                          {dayLabel}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-[12px] font-medium text-gray-900 dark:text-slate-200 truncate block">
                          {h.name}
                        </span>
                        <span className="text-[10px] text-gray-400 dark:text-slate-500">
                          {fmtDate(h.holiday_date)} · {dt.toLocaleDateString("en-US", { weekday: "short" })}
                        </span>
                      </div>
                      <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${
                        jur === "US"
                          ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                          : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                      }`}>
                        {jur}
                      </span>
                    </div>
                  );
                })}
              </div>
              <a
                href="/app1/people/timesheet/holidays"
                className="block text-center text-[10px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline py-2 border-t border-neutral-100 dark:border-slate-700"
              >
                View full calendar →
              </a>
            </div>
          )}
        </div>

        {/* ── Q2: Pipeline Pulse (top-right) — only for users with leads.view ── */}
        {canSeePipeline && <div className="flex flex-col">
          <DashboardCard
            title={
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                Pipeline pulse
                <HeaderInfoIcon tooltip="Real-time health of the active BD pipeline across the team." />
              </span>
            }
            icon={ICON_PIPELINE}
            subtitle={`${pipeline.total_active || 0} active deals`}
          >
            <div className="px-4 py-3">
              {/* Stage counts row */}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-3">
                {(pipeline.stages || []).map((s) => {
                  const maxCount = Math.max(...(pipeline.stages || []).map(x => x.count), 1);
                  return (
                    <div key={s.stage} className="text-center">
                      <div className="text-xl font-bold text-gray-900 dark:text-slate-100">{s.count}</div>
                      <div className="text-[10px] font-medium text-neutral-400 dark:text-slate-500 mt-0.5">
                        {s.label}
                      </div>
                      <div className="w-full h-1 rounded-full mt-1.5 bg-neutral-200 dark:bg-slate-700">
                        <div
                          className="h-full rounded-full transition-all bg-brand-blue"
                          style={{ width: `${Math.max(5, (s.count / Math.max(maxCount, 8)) * 100)}%` }}
                        />
                      </div>
                      <div className="text-[10px] mt-1 text-gray-500 dark:text-slate-400">
                        {fmtMoney(s.total_value)}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Throughput line */}
              {pipeline.throughput && (
                <div className="py-2 px-3 my-2 text-[11px] border-y border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 flex flex-wrap items-center gap-x-1 gap-y-0.5">
                  Today:{" "}
                  <span className="text-gray-900 dark:text-slate-200">{pipeline.throughput.scored_today}</span> scored
                  <span className="text-gray-400 dark:text-slate-500">{" → "}</span>
                  <span className="text-gray-900 dark:text-slate-200">{pipeline.throughput.passed_review_today}</span> passed review
                  <span className="text-gray-400 dark:text-slate-500">{" → "}</span>
                  <span className="text-brand-blue dark:text-blue-300">{pipeline.throughput.dispositioned_today}</span> dispositioned
                  <InfoIcon tooltip="Today's screening funnel: how many leads were scored by AI, passed initial review, and received a disposition decision." />
                </div>
              )}

              {/* Attention required */}
              {(pipeline.attention || []).length > 0 && (
                <div className="mt-2">
                  <div className="text-[11px] font-semibold mb-2 text-neutral-500 dark:text-slate-400 flex items-center gap-1.5">
                    <Warning size={13} weight="fill" className="text-amber-500 flex-shrink-0" />
                    Attention required
                    <InfoIcon tooltip="Deals with the worst health scores across the active pipeline — stale activity, overdue tasks, or missing information." />
                  </div>
                  <div className="space-y-1">
                    {pipeline.attention.map((item) => (
                      <a
                        key={item.opportunity_id}
                        href={`/crm/opportunity/${item.opportunity_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-2 py-1.5 px-2 rounded transition-all hover:bg-gray-50 dark:hover:bg-slate-700/50 ${
                          newItemIds.has(item.opportunity_id) ? "border-l-2 border-l-brand-blue" : ""
                        }`}
                      >
                        <Tooltip text="Deal health: Green = healthy, Amber = watch, Red = at risk. Based on activity recency, task completion, and deadline proximity." inline>
                          <HealthDot health={item.health} size={10} />
                        </Tooltip>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            {item.display_agency && <AgencyBadge agency={item.display_agency} />}
                            <span className="text-[11px] font-medium truncate text-gray-900 dark:text-slate-200">
                              {item.opportunity_title}
                            </span>
                          </div>
                          <p className="text-[10px] text-gray-400 dark:text-slate-500">
                            {item.health_reason}
                          </p>
                        </div>
                        <span className="text-[11px] font-mono flex-shrink-0 text-gray-500 dark:text-slate-400">
                          {fmtMoney(item.estimated_value)}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {(!pipeline.attention || pipeline.attention.length === 0) && (
                <div className="text-center py-4">
                  <span className="text-[10px] text-gray-400 dark:text-slate-500">No deals requiring attention</span>
                </div>
              )}
            </div>
          </DashboardCard>
        </div>}

        {/* ── Q3: Response Tracker (bottom-left) — only for users with leads.view ── */}
        {canSeeResponses && <div className="flex flex-col">
          <DashboardCard
            title={<>Response tracker<InfoIcon tooltip="Solicitation responses with approaching deadlines. Red means take action now." position="bottom" /></>}
            icon={ICON_RESPONSES}
            subtitle={`${(responses.items || []).filter(i => i.days_remaining <= 7).length} due within 7 days`}
          >
            {(!responses.items || responses.items.length === 0) ? (
              <div className="flex flex-col items-center justify-center py-12 gap-2">
                <Envelope size={36} weight="thin" className="text-neutral-300 dark:text-slate-600" />
                <span className="text-xs font-medium text-neutral-500 dark:text-slate-400">No active responses</span>
                <span className="text-[11px] text-neutral-400 dark:text-slate-500">Responses will appear here when in flight</span>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-slate-700">
                {responses.items.map((item) => (
                  <a
                    key={item.opportunity_id}
                    href={`/crm/opportunity/${item.opportunity_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-3 px-4 py-2.5 transition-all hover:bg-gray-50 dark:hover:bg-slate-700/50 ${
                      item.days_remaining <= 2 ? "bg-red-50/50 dark:bg-red-900/10" : ""
                    }`}
                  >
                    {/* Countdown block */}
                    <div className="flex flex-col items-center justify-center flex-shrink-0" style={{ width: 50 }}>
                      <span
                        className={`text-base font-bold ${
                          item.days_remaining <= 2
                            ? "text-red-600 dark:text-red-400"
                            : item.days_remaining <= 7
                              ? "text-amber-600 dark:text-amber-400"
                              : "text-gray-500 dark:text-slate-400"
                        }`}
                      >
                        {item.days_remaining < 0 ? `${item.days_remaining}` : item.days_remaining}
                      </span>
                      <span className="text-[9px] text-gray-400 dark:text-slate-500">
                        {fmtDate(item.response_deadline)}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        {item.display_agency && <AgencyBadge agency={item.display_agency} />}
                        <span className="text-[12px] font-medium truncate text-gray-900 dark:text-slate-200">
                          {item.opportunity_title}
                        </span>
                      </div>
                      <p className="text-[10px] text-gray-400 dark:text-slate-500">
                        {item.response_type || item.stage}
                        {item.total_tasks > 0 && (
                          <span className="text-gray-500 dark:text-slate-400">
                            {" · "}{item.completed_tasks}/{item.total_tasks} tasks
                          </span>
                        )}
                      </p>
                    </div>

                    <Tooltip text="On Track = progressing on schedule. At Risk = tasks overdue or idle 48+ hours. Blocked = multiple issues or no progress near deadline." inline>
                      <StatusBadge status={item.response_status} />
                    </Tooltip>
                  </a>
                ))}
              </div>
            )}
          </DashboardCard>
        </div>}

        {/* ── Q4: Intelligence Monitor (bottom-right) — only for users with intel.view ── */}
        {canSeeIntel && <div className="flex flex-col">
          <DashboardCard
            title={<>Intelligence monitor<InfoIcon tooltip="High-relevance intelligence from the last 48 hours, scored against your active pipeline." position="bottom" /></>}
            icon={ICON_INTELLIGENCE}
            subtitle="Last 48 hours"
          >
            {(!intelligence.items || intelligence.items.length === 0) ? (
              <div className="flex flex-col items-center justify-center py-12 gap-2">
                <SpeakerSimpleX size={36} weight="thin" className="text-neutral-300 dark:text-slate-600" />
                <span className="text-xs font-medium text-neutral-500 dark:text-slate-400">No high-relevance intel</span>
                <span className="text-[11px] text-neutral-400 dark:text-slate-500">New signals from the last 48h will appear here</span>
              </div>
            ) : (
              <div className="p-3 space-y-2">
                {intelligence.items.map((item) => {
                  const colors = INTEL_COLORS[item.intel_type] || { light: "#6b7280", dark: "#94a3b8" };
                  const typeColor = isDark ? colors.dark : colors.light;
                  return (
                    <div
                      key={item.intel_id}
                      className={`rounded-md p-3 cursor-pointer transition-all hover:bg-gray-100 dark:hover:bg-slate-700/50 bg-gray-50 dark:bg-slate-800/50 ${
                        newItemIds.has(item.intel_id) ? "ring-1 ring-brand-blue" : ""
                      }`}
                      style={{ borderLeft: `3px solid ${typeColor}` }}
                      onClick={() => {
                        if (item.source_url) {
                          window.open(item.source_url, "_blank", "noopener");
                        } else if (item.linked_opportunity_id) {
                          window.open(`/crm/opportunity/${item.linked_opportunity_id}`, "_blank", "noopener");
                        }
                      }}
                    >
                      {/* Top line */}
                      <div className="flex items-center gap-2 mb-1">
                        <Tooltip text="COMPETITIVE = competitor activity on your deals. STAKEHOLDER = personnel or budget changes at target agencies. PROACTIVE = automation opportunity identified." inline>
                          <span
                            className="text-[9px] font-semibold tracking-wide px-1.5 py-0.5 rounded"
                            style={{ color: typeColor, background: `${typeColor}20` }}
                          >
                            {item.intel_type}
                          </span>
                        </Tooltip>
                        {item.agency && <AgencyBadge agency={item.agency} />}
                        <span className="flex-1" />
                        <span className="text-[10px] text-gray-400 dark:text-slate-500">
                          {fmtAge(item.age_hours)}
                        </span>
                        {item.relevance_score != null && (
                          <Tooltip text="AI-scored relevance to your active pipeline. Higher scores indicate direct impact on deals you're pursuing." inline>
                            <span className="text-[11px] font-bold text-brand-blue dark:text-blue-300">
                              {item.relevance_score}
                            </span>
                          </Tooltip>
                        )}
                      </div>

                      {/* Summary */}
                      <p
                        className="text-[12px] leading-relaxed text-gray-700 dark:text-slate-300"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item.summary}
                      </p>

                      {/* Linked opportunity */}
                      {item.linked_opportunity_title && (
                        <p className="text-[10px] mt-1 text-brand-blue dark:text-blue-300">
                          ↳ Linked: {item.linked_opportunity_title}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </DashboardCard>
        </div>}
      </div>

      {/* ── Staffing Pipeline Strip (STAFFING-1) — only for users with leads.view,
          matching the /staffing sidebar page's own permission gate ─────── */}
      {canSeeStaffing && staffing && staffing.total_active > 0 && (
        <div className="px-5 sm:px-6 lg:px-8 pb-6 lg:pb-8 -mt-2 max-w-3xl">
          <DashboardCard
            title={<>Staffing pipeline<InfoIcon tooltip="Key Talent staffing leads from automated SAM.gov contract award scanning. Click to manage the full pipeline." /></>}
            icon={ICON_STAFFING}
            subtitle={`${staffing.total_active} active leads`}
          >
            <div className="p-3">
              {/* Stats strip */}
              <div className="flex items-center gap-6 mb-3">
                <div className="text-center">
                  <div className="text-lg font-bold text-gray-900 dark:text-white">{staffing.total_active}</div>
                  <div className="text-[10px] text-gray-400 dark:text-slate-500">Active</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600 dark:text-blue-400">{staffing.new_this_week}</div>
                  <div className="text-[10px] text-gray-400 dark:text-slate-500">New</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-amber-600 dark:text-amber-400">{staffing.awaiting_outreach}</div>
                  <div className="text-[10px] text-gray-400 dark:text-slate-500">Awaiting</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600 dark:text-green-400">{staffing.in_progress}</div>
                  <div className="text-[10px] text-gray-400 dark:text-slate-500">Engaged</div>
                </div>
                <div className="flex-1" />
                <a
                  href="/staffing"
                  className="text-xs text-brand-blue dark:text-blue-300 hover:underline font-medium"
                >
                  View all ({staffing.total_active}) →
                </a>
              </div>

              {/* Recent leads — capped at 5, full list lives at /staffing.
                  Fixed-width grid columns instead of flex so the row reads
                  as an aligned table instead of two clusters with a wide
                  gap between them on this full-width card. */}
              {staffing.recent && staffing.recent.length > 0 && (
                <div>
                  <div className="grid grid-cols-[1fr_160px_50px_90px] gap-3 px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500">
                    <span>Company</span>
                    <span>Agency</span>
                    <span className="text-center">Score</span>
                    <span>Status</span>
                  </div>
                  <div className="divide-y divide-gray-100 dark:divide-slate-700">
                    {staffing.recent.slice(0, 5).map((lead) => (
                      <a
                        key={lead.id}
                        href={`/staffing/lead/${lead.id}`}
                        className="grid grid-cols-[1fr_160px_50px_90px] items-center gap-3 px-2 py-2 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                      >
                        <span className="text-[12px] font-medium truncate text-gray-900 dark:text-slate-200 min-w-0">
                          {lead.company_name}
                        </span>
                        <span className="text-[11px] text-gray-500 dark:text-slate-400 truncate">
                          {lead.award_agency || "—"}
                        </span>
                        <span className="text-center">
                          {lead.relevance_score != null && (
                            <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded ${
                              lead.relevance_score >= 8 ? "text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/30"
                                : lead.relevance_score >= 6 ? "text-amber-700 bg-amber-100 dark:text-amber-400 dark:bg-amber-900/30"
                                : "text-gray-500 bg-gray-100 dark:text-slate-400 dark:bg-slate-700"
                            }`}>
                              {lead.relevance_score}
                            </span>
                          )}
                        </span>
                        <span>
                          <span className={`inline-block text-[9px] px-1.5 py-0.5 rounded-full ${
                            lead.status === "new" ? "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400"
                              : lead.status === "outreach_sent" ? "bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400"
                              : "bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-slate-400"
                          }`}>
                            {lead.status.replace(/_/g, " ")}
                          </span>
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DashboardCard>
        </div>
      )}

      {/* v1.79 — shared TaskDismissModal component (extracted from v1.77's
          inline modal so the same UX works on TaskRegistry + MR detail). */}
      <TaskDismissModal
        open={!!dismissModal}
        task={dismissModal}
        onClose={() => setDismissModal(null)}
        onDismissed={(taskId) =>
          setDismissedIds((prev) => new Set([...prev, taskId]))
        }
      />
    </div>
  );
}
